import { type CSSProperties } from 'styled-components/macro'

import { Field, Layout } from '@loadsmart/miranda-react'
import { getToken } from '@loadsmart/miranda-react/dist/tokens'
import { type FetchLocationOptions } from '@loadsmart/react-location-select'

import { LocationSelect, type LocationSelectSingleProps } from '../location/LocationSelect'

type BaseProps<T> = Omit<LocationSelectSingleProps<T>, 'value' | 'onChange' | 'multiple' | 'name'>

export type LaneSelectProps<T> = {
  config?: FetchLocationOptions
  pickupValue?: T
  deliveryValue?: T
  pickupLabel?: string
  deliveryLabel?: string
  pickupPlaceholder?: string
  deliveryPlaceholder?: string
  pickupErrorHint?: string
  deliveryErrorHint?: string
  style?: CSSProperties
  onChange: (newValue: { pickup?: T; delivery?: T }) => void
} & BaseProps<T>

export function LaneSelect<T>({
  config,
  pickupValue,
  deliveryValue,
  pickupLabel,
  deliveryLabel,
  pickupPlaceholder = 'Pickup address',
  deliveryPlaceholder = 'Delivery address',
  pickupErrorHint,
  deliveryErrorHint,
  style,
  onChange,
  ...props
}: Readonly<LaneSelectProps<T>>) {
  function handleChangeLocation(field: 'pickup' | 'delivery', newValue?: T): void {
    onChange({
      pickup: pickupValue,
      delivery: deliveryValue,
      [field]: newValue, // overrides pickup or delivery
    })
  }

  return (
    <Layout.Grid gap="spacing-4" min-column-width={getToken('spacing-6')} style={style}>
      <Field status={pickupErrorHint ? 'danger' : 'default'}>
        {pickupLabel && <Field.Label id="label-pickup">{pickupLabel}</Field.Label>}
        <LocationSelect
          id="pickup"
          name="pickup"
          aria-labelledby="label-pickup"
          placeholder={pickupPlaceholder}
          value={pickupValue}
          multiple={false}
          onChange={value => handleChangeLocation('pickup', value)}
          config={config}
          {...props}
        />
        {pickupErrorHint && <Field.Hint>{pickupErrorHint}</Field.Hint>}
      </Field>
      <Field status={deliveryErrorHint ? 'danger' : 'default'}>
        {deliveryLabel && <Field.Label id="label-delivery">{deliveryLabel}</Field.Label>}
        <LocationSelect
          id="delivery"
          name="delivery"
          placeholder={deliveryPlaceholder}
          value={deliveryValue}
          multiple={false}
          onChange={value => handleChangeLocation('delivery', value)}
          config={config}
          {...props}
        />
        {deliveryErrorHint && <Field.Hint>{deliveryErrorHint}</Field.Hint>}
      </Field>
    </Layout.Grid>
  )
}
